import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Card, CardContent } from '../ui/card';
import { Rocket, Building2, Bot, GraduationCap, Globe } from 'lucide-react';

const StrategicPillars = () => {
  const { t } = useTranslation();

  const pillars = [
    {
      id: 1,
      icon: Rocket,
      title: t('vision30.pillars.digitalEconomy.title'),
      description: t('vision30.pillars.digitalEconomy.description'),
      details: t('vision30.pillars.digitalEconomy.details')
    },
    {
      id: 2,
      icon: Building2,
      title: t('vision30.pillars.smartInfrastructure.title'),
      description: t('vision30.pillars.smartInfrastructure.description'),
      details: t('vision30.pillars.smartInfrastructure.details')
    },
    {
      id: 3,
      icon: Bot,
      title: t('vision30.pillars.aiAutomation.title'),
      description: t('vision30.pillars.aiAutomation.description'),
      details: t('vision30.pillars.aiAutomation.details')
    },
    {
      id: 4,
      icon: GraduationCap,
      title: t('vision30.pillars.educationSkills.title'),
      description: t('vision30.pillars.educationSkills.description'),
      details: t('vision30.pillars.educationSkills.details')
    },
    {
      id: 5,
      icon: Globe,
      title: t('vision30.pillars.globalCompetitiveness.title'),
      description: t('vision30.pillars.globalCompetitiveness.description'),
      details: t('vision30.pillars.globalCompetitiveness.details')
    }
  ];

  return (
    <section className="py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('vision30.pillars.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('vision30.pillars.subtitle')}
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <Card 
              key={pillar.id} 
              className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 bg-background/50 backdrop-blur-sm"
            >
              <CardContent className="p-8">
                {/* Icon */}
                <div className="text-6xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {React.createElement(pillar.icon, { className: "h-16 w-16 text-primary" })}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                  {pillar.title}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {pillar.description}
                </p>

                {/* Details */}
                <p className="text-sm text-muted-foreground/80 leading-relaxed">
                  {pillar.details}
                </p>

                {/* Progress Indicator */}
                <div className="mt-6">
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                    <span>{t('vision30.pillars.progress')}</span>
                    <span>{Math.min(20 + (index * 15), 100)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${Math.min(20 + (index * 15), 100)}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-muted-foreground mb-6">
            {t('vision30.pillars.cta')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors">
              {t('vision30.pillars.ctaButton')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StrategicPillars;
